import {useMatchChain, useToast, useUserInfo} from "../../hooks";
import {useEffect, useMemo, useState} from "react";
import {Abi} from "abitype";
import {FormattedMessage, useIntl} from "react-intl";
import {useQueryClient} from "@tanstack/react-query";
import {userImportTokenApi} from "../../api";
import {isSuccess} from "../../api/request";
import {USER_IMPORT_TOKEN_LIST_KEY} from "../../hooks/api/wallet";
import {Field, Input} from "../../ui";
import {Button} from "../index";
import {defineChain, erc20Abi} from "viem";
import useIsContract from "../../hooks/useIsContract";
import {Lottie} from "../../ui";
import {successImg, failImg} from "assets/lottie";

export default function ImportToken ({close}: {
    close: () => void
}) {
    const [status,setStatus] = useState<''|'success'|'fail'>('')
    const {token} = useUserInfo()
    const [address, setAddress] = useState("")
    const [symbol, setSymbol] = useState("")
    const [decimals, setDecimals] = useState("")
    const [error, setError] = useState<{
        address?: string,
        symbol?: string,
        decimals?: string
    }>({})
    const {publicClient, chainId,chain} = useMatchChain()
    const getContractInfo = async () => {
        if (!publicClient) return;

        const calls = [
            {
                address: address as `0x${string}`,
                abi: erc20Abi as Abi,
                functionName: "symbol",
                args: []
            },
            {
                address: address as `0x${string}`,
                abi: erc20Abi as Abi,
                functionName: "decimals",
                args: []
            },

        ];
        try {
            const results = await publicClient.multicall({contracts: calls});
            console.log('results', results)

            if (!symbol && results[0]?.status == 'success') {
                setSymbol(results[0]?.result as string)
            }
            if (!decimals && results[1]?.status == 'success') {
                setDecimals((results[1]?.result as number).toString())
            }
        } catch (error: any) {
            setError({
                address: error.message
            })
        }

    }
    const intl = useIntl()
    useEffect(() => {
        if (address.length === 42) {
            const reg = /^0x[0-9a-fA-F]{40}$/;
            if (!reg.test(address)) {
                setError({
                    ...error,
                    address: intl.formatMessage({
                        id: "tokenAddressError"
                    })
                })
            } else {
                setError({
                    ...error,
                    address: ""
                })
                publicClient && getContractInfo()
            }
        }
    }, [address, publicClient]);
    const [loading, setLoading] = useState(false)
    const toast = useToast()
    const queryClient = useQueryClient();

    const onImport = async () => {
        setLoading(true)
        try {
            const res = await userImportTokenApi({
                chain_id: chainId?.toString() || '',
                symbol,
                address,
                decimals
            })
            if (isSuccess(res)) {
                // toast.success(intl.formatMessage({
                //     id: "importSuccess"
                // }))
                setStatus('success')
                queryClient.invalidateQueries({
                    queryKey: [USER_IMPORT_TOKEN_LIST_KEY, token, chainId]
                })
                // close()
            } else {
                setStatus('fail')
                toast.error(res.message)
            }
        } catch (error: any) {
            setStatus('fail')
            toast.error(error.message)
        } finally {
            setLoading(false)
        }

    }

    const canImport = useMemo(() => {
        if (!address) {
            return false
        }
        if (!symbol) {
            return false
        }
        if (decimals.length === 0) {
            return false
        }
        const reg = /^0x[0-9a-fA-F]{40}$/;
        if (!reg.test(address)) {
            return false
        }
        return true;
    }, [error, address, symbol, decimals])

    const isContractQuery = useIsContract({
        //@ts-ignore
        chain:defineChain(chain),
        address,
        enabled:canImport
    })

    if(status=='success'||status=='fail'){
        return <div className={`matchid-import-token-result matchid-flex`}>
            <div className={`matchid-import-token-result-box matchid-flex`}>
                <div className={`matchid-import-token-result-img`}>
                    <Lottie animationData={status=='success'?successImg:failImg} style={{
                        width: "96px",
                        height: "96px",
                    }} />
                </div>
                <div className={`matchid-import-token-result-text matchid-import-token-result-text-${status}`}>
                    {status=='success'?<FormattedMessage id={"importSuccess"}/>:<FormattedMessage id={"importFail"}/>}
                </div>
            </div>
            {status=='success' ? <Button size={"lg"} onClick={close} block highlight><FormattedMessage id={"close"}/></Button>
                : <Button size={"lg"} onClick={()=>setStatus('')} block highlight><FormattedMessage id="back"/></Button>}
        </div>
    }

    return <div className={`matchid-import-token`}>
        <div className={"matchid-import-token-form"}>
            <Field label={intl.formatMessage({
                id: "tokenSmartContract"
            })} error={error.address ? error.address : (
                isContractQuery.isFetched&&!isContractQuery.isLoading&&!isContractQuery.data&&canImport?"Address isn't a contract address":''
            )}>
                <Input placeholder={intl.formatMessage({
                    id: "tokenSmartContractPlaceholder"
                })} value={address}
                       onChange={(e) => setAddress(e.target.value)} maxLength={42}/>
            </Field>
            <Field label={intl.formatMessage({
                id: "tokenSymbol"
            })} error={error.symbol}>
                <Input placeholder={intl.formatMessage({
                    id: "tokenSymbolPlaceholder"
                })} value={symbol} onChange={(e) => setSymbol(e.target.value)}
                       maxLength={16}/>
            </Field>
            <Field label={intl.formatMessage({
                id: "tokenDecimals"
            })} error={error.decimals}>
                <Input placeholder={intl.formatMessage({
                    id: "tokenDecimalsPlaceholder"
                })} type={"number"} value={decimals}
                       onChange={(e) => setDecimals(e.target.value)}/>
            </Field>
        </div>
        <Button size={"lg"} onClick={onImport} block loading={loading||isContractQuery.isLoading}
                disabled={!canImport||!isContractQuery.data} highlight><FormattedMessage id={"import"}/></Button>
    </div>
}